.root {
  --sel-w: 44px;

  min-width: 0;
  overflow: hidden;
  border: 1px solid var(--line);
  border-radius: var(--radius-card);
  background: var(--panel);
  box-shadow: var(--shadow-panel);
}

.scroll {
  overflow: auto;
}

.scroll:focus-visible {
  outline-offset: -2px;
}

.table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: var(--t-dense);
}

/* ---- header ---- */
.th {
  position: sticky;
  z-index: 2;
  top: 0;
  padding: 0 14px;
  border-bottom: 1px solid var(--line);
  background: var(--panel-2);
  color: var(--muted);
  font-size: var(--t-min);
  font-weight: 650;
  text-align: left;
  white-space: nowrap;
}

.thInner {
  display: flex;
  align-items: center;
  gap: 2px;
  min-height: 38px;
}

.align_right .thInner {
  justify-content: flex-end;
}

.align_center .thInner {
  justify-content: center;
}

.align_left {
  text-align: left;
}

.align_right {
  text-align: right;
  font-variant-numeric: lining-nums tabular-nums;
}

.align_center {
  text-align: center;
}

.sortBtn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  min-height: var(--target-min);
  padding: 0 4px;
  margin: 0 -4px;
  border: 0;
  border-radius: var(--radius-ctl);
  background: none;
  color: inherit;
  font: inherit;
  cursor: pointer;
}

.sortBtn:hover {
  color: var(--ink);
}

.th[aria-sort="ascending"] .sortBtn,
.th[aria-sort="descending"] .sortBtn {
  color: var(--ink);
}

.sortIdle {
  opacity: 0.55;
}

.iconBtn {
  display: inline-grid;
  place-items: center;
  width: var(--target-min);
  height: var(--target-min);
  padding: 0;
  border: 0;
  border-radius: var(--radius-ctl);
  background: none;
  color: var(--muted);
  cursor: pointer;
}

.iconBtn:hover {
  background: var(--panel-3);
  color: var(--ink);
}

/* The header menu button is quiet until the header is hovered or something in it is focused. */
.menuBtn {
  opacity: 0;
}

.th:hover .menuBtn,
.th:focus-within .menuBtn,
.menuBtn[aria-expanded="true"] {
  opacity: 1;
}

@media (hover: none) {
  .menuBtn {
    opacity: 1;
  }
}

/* ---- body ---- */
.td {
  padding: 11px 14px;
  border-bottom: 1px solid var(--line);
  background: var(--panel);
  color: var(--ink);
  vertical-align: middle;
}

.table tbody tr:last-child .td {
  border-bottom: 0;
}

[data-density="compact"] .td {
  padding-top: 5px;
  padding-bottom: 5px;
}

[data-density="compact"] .thInner {
  min-height: 32px;
}

.row {
  transition: background var(--dur-1) var(--ease-std);
}

.row:hover .td {
  background: var(--panel-2);
}

.rowOpen {
  cursor: pointer;
}

.rowOpen:focus-visible {
  outline-offset: -2px;
}

.rowOpen:focus-visible .td {
  background: var(--accent-w);
}

.rowSelected .td {
  background: var(--accent-w);
}

.rowSelected:hover .td {
  background: var(--accent-w);
}

/* ---- sticky first column ---- */
.stickyCol {
  position: sticky;
  z-index: 1;
  box-shadow: 1px 0 0 var(--line);
}

th.stickyCol {
  z-index: 3;
}

/* ---- selection ---- */
.selCol {
  width: var(--sel-w);
  min-width: var(--sel-w);
  padding-right: 0;
  padding-left: 14px;
}

.check {
  width: 16px;
  height: 16px;
  margin: 0;
  accent-color: var(--accent);
  cursor: pointer;
}

/* ---- hover actions ---- */
.actionsCol {
  width: 1%;
  padding-left: 6px;
  white-space: nowrap;
  text-align: right;
}

.row .actionsCol > * {
  opacity: 0;
  transition: opacity var(--dur-1) var(--ease-std);
}

.row:hover .actionsCol > *,
.row:focus-within .actionsCol > * {
  opacity: 1;
}

@media (hover: none) {
  .row .actionsCol > * {
    opacity: 1;
  }
}

/* ---- states ---- */
.stateCell {
  padding: 0;
}

.skRow .td {
  padding-top: 14px;
  padding-bottom: 14px;
}

/* ---- footer ---- */
.footer {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px 16px;
  padding: 8px 14px;
  border-top: 1px solid var(--line);
  background: var(--panel-2);
  color: var(--muted);
  font-size: var(--t-dense);
  font-variant-numeric: lining-nums tabular-nums;
}

.footLeft,
.footRight {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 14px;
}

.selectedCount {
  color: var(--accent);
  font-weight: 650;
}

.footBtn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  min-height: var(--target-min);
  padding: 0 10px;
  border: 1px solid var(--line-2);
  border-radius: var(--radius-ctl);
  background: var(--panel);
  color: var(--ink-2);
  font-size: var(--t-dense);
  cursor: pointer;
}

.footBtn:hover:not(:disabled) {
  border-color: var(--accent-line);
  color: var(--accent);
}

.footBtn:disabled {
  color: var(--disabled);
  cursor: default;
}

.pager {
  display: flex;
  align-items: center;
  gap: 8px;
}

.pageOf {
  color: var(--ink-2);
}

.seg {
  display: inline-flex;
  overflow: hidden;
  border: 1px solid var(--line-2);
  border-radius: var(--radius-ctl);
  background: var(--panel);
}

.segBtn {
  display: grid;
  place-items: center;
  width: 30px;
  height: var(--target-min);
  padding: 0;
  border: 0;
  background: none;
  color: var(--muted);
  cursor: pointer;
}

.segBtn[aria-pressed="true"] {
  background: var(--accent);
  color: var(--on-fill);
}

/* ---- menu (portalled: doubled selector outranks the card's own padding) ---- */
.menu.menu {
  min-width: 170px;
  padding: 4px;
}

.menuItem {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-height: 32px;
  padding: 5px 10px 5px 6px;
  border: 0;
  border-radius: var(--radius-ctl);
  background: none;
  color: var(--ink);
  font: inherit;
  font-size: var(--t-dense);
  text-align: left;
  cursor: pointer;
}

.menuItem:hover:not(:disabled),
.menuItem:focus-visible {
  background: var(--accent-w);
}

.menuItem:disabled {
  color: var(--disabled);
  cursor: default;
}

.menuTick {
  flex: 0 0 14px;
  color: var(--accent);
  text-align: center;
}

@media (width <= 900px) {
  .th,
  .td {
    padding-right: 10px;
    padding-left: 10px;
  }

  .sortBtn,
  .iconBtn,
  .footBtn,
  .segBtn {
    min-height: var(--target-touch);
  }

  .iconBtn,
  .segBtn {
    width: var(--target-touch);
  }

  .menuItem {
    min-height: var(--target-touch);
  }

  .check {
    width: 20px;
    height: 20px;
  }
}
